<script setup lang="ts">
import {ExtendedProject} from "@/types";
import {computed, ref, watch} from "vue";
import {router} from "@inertiajs/vue3";
import SendcryptLogo from "@/components/SendcryptLogo.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";

interface Props {
    project: ExtendedProject
}
const props = defineProps<Props>()

const isDialogOpen = ref(false)
const isConfirmed = ref(false)
const isLoading = ref(false)

watch(isDialogOpen, (isOpen) => {
    if (isOpen) {
        isConfirmed.value = false
    }
})

const label = computed(() => {
    if (isConfirmed.value) {
        return `I have read and understood theses effects`
    }

    return 'I want to delete this project'
})

const remove = () => {
    if (!isConfirmed.value) {
        isConfirmed.value = true
        return
    }

    router.delete(route('projects.destroy', {project: props.project.slug}), {
        onStart: () => {
            isLoading.value = true
        },
        onFinish: () => {
            isLoading.value = false
        },
        onSuccess: () => {
            isDialogOpen.value = false
        }
    })
}
</script>

<template>
    <v-dialog
        v-model="isDialogOpen"
        max-width="500"
        overlay-opacity="0.4"
    >
        <template #activator="{ props: attrs }">
            <default-btn
                v-bind="attrs"
                size="small"
                variant="text"
                rounded="lg"
                class="font-weight-medium text-error rounded border bg-grey-lighten-5"
            >
                Delete this project
            </default-btn>
        </template>

        <v-card
            :flat="true"
            rounded="lg"
        >
            <v-card-title class="font-weight-bold border-bottom text-body-2 pt-4">
                {{ `Delete ${props.project.name}` }}
                <v-btn
                    variant="plain"
                    size="small"
                    icon="mdi-close"
                    class="float-end pb-2"
                    @click="isDialogOpen = false"
                />
            </v-card-title>

            <v-card-text class="pt-3 text-black text-center">
                <sendcrypt-logo />
                <div class="text-h6 font-weight-bold mb-1">
                    {{ props.project.name }}
                </div>
                <div class="text-body-2">
                    <v-icon size="small">
                        mdi-account-group-outline
                    </v-icon> {{ props.project.users_count }} collaborators
                    <v-icon
                        size="small"
                        class="ml-3"
                    >
                        mdi-cloud-upload-outline
                    </v-icon> {{ props.project.uploads_count }} transfers
                </div>
                <v-divider
                    v-if="isConfirmed"
                    class="my-5"
                />
                <error-alert v-if="isConfirmed">
                    Unexpected bad things will happen if you don't read this!
                </error-alert>
                <ul
                    v-if="isConfirmed"
                    class="mx-15 lh-1-5 mt-3 text-start text-grey-darken-3 text-body-2"
                >
                    <li class="pl-1">
                        This will immediately delete the <b>{{ props.project.name }}</b> project, transfers, SFTP configuration and remove all collaborators associated with this project.
                    </li>
                    <li class="pl-1">
                        You will no longer be able to access the <b>{{ props.project.name }}</b> project, and after 90 days all the project data will be permanently deleted.
                    </li>
                </ul>
            </v-card-text>

            <v-divider />

            <v-card-actions class="px-5 py-4">
                <default-btn
                    class="border"
                    rounded="lg"
                    :block="true"
                    :loading="isLoading"
                    @click="remove"
                >
                    {{ label }}
                </default-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
:deep(.v-dialog--active) {
  border-radius: 16px !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.lh-1-5 {
  line-height: 1.5 !important;
}
</style>
